import { DeleteRounded, ExpandMore } from "@mui/icons-material";
import { Accordion, AccordionDetails, AccordionSummary, Grid, IconButton, TextField } from "@mui/material";
import { FunctionComponent, SyntheticEvent, useEffect, useState } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { useRecoilValue } from "recoil";
import { CBCountry, CBCreationOfProps, CBGoogleAutocompleteType, CBValuableLength, IFormValues } from "../types";
import CBFormTextFieldController from "./formController/CBFormTextFieldController";
import CBCountryController from "./formController/CBCountryController";
import CBProvinceController from "./formController/CBProvinceController";
import { provincesState } from "../state/customerbaseState";
import CBGoogleAutocompleteSearchBar from "./CBGoogleAutocompleteSearchBar";
import { extractGooglePlaceLocalityLongName, extractGooglePlaceTypeLongName, extractGooglePlaceTypeShortName } from "../utils";
import UpdateBtnsAccordion from "./UpdateBtnsAccordion";

interface CBContactPersonDetailsProps extends CBCreationOfProps {
    defaultExpanded?: boolean,
    isExpanded: boolean,
    index?: number,
    editing: boolean,
    isNew: boolean,
    onRemove: (event: SyntheticEvent, index: number) => void
}

const CustomerBaseContactPersonDetails: FunctionComponent<CBContactPersonDetailsProps> = ({
    creationOf,
    defaultExpanded = false,
    isExpanded,
    index = 0,
    editing,
    isNew,
    onRemove
}) => {

    const { t } = useTranslation('customerbase');
    const isCreationMode = !!creationOf;
    const { control, getValues, setValue, resetField, trigger } = useFormContext<IFormValues>(); 
    const provinceOptions = useRecoilValue(provincesState);
    const currentCountry = useWatch<IFormValues, `cbCorporateInfo.contactPersons.${typeof index}.country`>({
        control: control,
        name: `cbCorporateInfo.contactPersons.${index}.country`
    });

    const hasId = !!getValues(`cbCorporateInfo.contactPersons.${index}.id`);
    const [exp, setExp] = useState(false);
    
    useEffect(() => {
        if((isCreationMode && !hasId) || (!isCreationMode && !editing) || (!isCreationMode && editing && !hasId))
            setExp(index == 0);

    }, [index, editing, hasId, isCreationMode]);

    return (
        <Accordion /*defaultExpanded={defaultExpanded}*/ expanded={exp} onChange={(_e, expanded) => setExp(expanded)}>
            <AccordionSummary expandIcon={ <ExpandMore/> }>
                <UpdateBtnsAccordion
                    label={isNew ?
                        t("tabs.accordionsLabels.cbNewContactPerson") + (index==0? "" : (" " + (index+1))) :
                        t("tabs.accordionsLabels.cbContactPerson") + (index==0? "" : (" " + (index+1)))
                    }
                    editing={editing}
                    isExpanded={isExpanded}
                    showRemove={/*(!getValues(`cbCorporateInfo.contactPersons.${index}.id`)) &&*/ (isCreationMode || editing)}
                    onRemove={(event) => onRemove(event, index)}
                />
                {/*<Grid container alignItems="center" width={"99%"}>
                    <Grid item md={6} xs={6}>
                        {isNew ?
                            t("tabs.accordionsLabels.cbNewContactPerson") + (index==0? "" : (" " + (index+1))) :
                            t("tabs.accordionsLabels.cbContactPerson") + (index==0? "" : (" " + (index+1)))
                        }
                    </Grid>
                    <Grid item md={6} xs={6} display="flex" justifyContent={"flex-end"}>
                        {isCreationMode && (index!=0) && 
                            <IconButton color={"accent"} onClick={(e) => onRemove(e, index)}>
                                <DeleteRounded fontSize="small"/>
                            </IconButton>
                        }
                    </Grid>
                </Grid>
                    */}
            </AccordionSummary>
            <AccordionDetails>
                <Grid container columnSpacing={3} rowSpacing={1}>
                    {/* !isCreationMode && 
                        <Grid item md={6} xs={12}>
                            <TextField 
                                fullWidth
                                InputProps={{
                                    readOnly: isCreationMode ? false : !editing
                                }}
                                id={`cp-cbCustomerId-${index}`}
                                label={t("contactPerson.cbCustomerId")}
                                {...register(`cbCorporateInfo.contactPersons.${index}.cbCustomerId`)}
                            />
                        </Grid>
                    */}
                    {/*!isCreationMode && 
                        <Grid item md={12} xs={12}>
                            <CBFormTextFieldController 
                                formName={`cbCorporateInfo.contactPersons.${index}.bpCode`}
                                idField={`cp-bpCode-${index}`}
                                label={"contactPerson.bpCode"}
                                readOnly
                                isDisabled={editing}
                            />
                        </Grid>
                    */}
                    <Grid item md={6} xs={12}>
                        <CBFormTextFieldController 
                            formName={`cbCorporateInfo.contactPersons.${index}.name`}
                            idField={`cp-name-${index}`}
                            label={"contactPerson.name"}
                            readOnly={isCreationMode ? false : !editing}
                            required  
                            inputProps={{
                                maxLength: CBValuableLength.MAX_CONTACT_PERSON_NAME_LENGTH
                            }}
                        />
                    </Grid>
                    <Grid item md={6} xs={12}>
                        <CBFormTextFieldController 
                            formName={`cbCorporateInfo.contactPersons.${index}.surname`}
                            idField={`cp-surname-${index}`}
                            label={"contactPerson.surname"}
                            readOnly={isCreationMode ? false : !editing}
                            required
                            inputProps={{
                                maxLength: CBValuableLength.MAX_CONTACT_PERSON_SURNAME_LENGTH
                            }}
                        />
                    </Grid>
                    {(isCreationMode || editing) && 
                        <Grid item md={12} xs={12} mt={1}>
                            <CBGoogleAutocompleteSearchBar 
                                idField={`cb-contact-person-${index}-google-autocomplete-search-bar`}
                                onPlaceSelected={(place) => {
                                    console.log(place);

                                    const country = extractGooglePlaceTypeShortName(place, CBGoogleAutocompleteType.COUNTRY);
                                
                                    if(!!country && country == CBCountry.ITALY) {
                                        const province = extractGooglePlaceTypeShortName(place, CBGoogleAutocompleteType.PROVINCE);
                                        setValue(`cbCorporateInfo.contactPersons.${index}.region`, province || '', {shouldDirty: true, shouldValidate: true});
                                    }
                                    
                                    const city = extractGooglePlaceLocalityLongName(place);
                                    const postalCode = extractGooglePlaceTypeLongName(place, CBGoogleAutocompleteType.POSTAL_CODE);
                                    const streetName = extractGooglePlaceTypeLongName(place, CBGoogleAutocompleteType.ROUTE);
                                    const streetNumber = extractGooglePlaceTypeLongName(place, CBGoogleAutocompleteType.STREET_NUMBER);

                                    setValue(`cbCorporateInfo.contactPersons.${index}.country`, country || '', {shouldDirty: true, shouldValidate: true});
                                    setValue(`cbCorporateInfo.contactPersons.${index}.city`, city || '', {shouldDirty: true, shouldValidate: true});
                                    setValue(`cbCorporateInfo.contactPersons.${index}.postalCode`, postalCode || '', {shouldDirty: true, shouldValidate: true});
                                    setValue(`cbCorporateInfo.contactPersons.${index}.address`, streetName || '', {shouldDirty: true, shouldValidate: true});
                                    setValue(`cbCorporateInfo.contactPersons.${index}.houseNumber`, streetNumber || '', {shouldDirty: true, shouldValidate: true});

                               
                                }}
                                onChange={(event) => {
                                    if(!event.currentTarget.value) {
                                        
                                        resetField(`cbCorporateInfo.contactPersons.${index}.country`);
                                        resetField(`cbCorporateInfo.contactPersons.${index}.region`);
                                        resetField(`cbCorporateInfo.contactPersons.${index}.city`);                                   
                                        resetField(`cbCorporateInfo.contactPersons.${index}.postalCode`);
                                        resetField(`cbCorporateInfo.contactPersons.${index}.address`);
                                        resetField(`cbCorporateInfo.contactPersons.${index}.houseNumber`);

                                        trigger([
                                            `cbCorporateInfo.contactPersons.${index}.country`,
                                            `cbCorporateInfo.contactPersons.${index}.region`,
                                            `cbCorporateInfo.contactPersons.${index}.city`,
                                            `cbCorporateInfo.contactPersons.${index}.postalCode`,
                                            `cbCorporateInfo.contactPersons.${index}.address`,
                                            `cbCorporateInfo.contactPersons.${index}.houseNumber`
                                        ]);
                                    }
                                }}
                            />
                        </Grid>
                    }
                    <Grid item md={6} xs={12}>
                        <CBCountryController
                            formName={`cbCorporateInfo.contactPersons.${index}.country`}
                            idField={`cp-country-${index}`}
                            label={"contactPerson.country"}
                            showSelect={isCreationMode || editing}
                            handleBlankedValueOnChange={() => {
                                setValue(`cbCorporateInfo.contactPersons.${index}.region`, '', {shouldDirty: true});
                            }}
                        />
                    </Grid>
                    <Grid item md={6} xs={12}>
                        <CBProvinceController 
                            key={`cp-region-controller-${index}`}
                            formName={`cbCorporateInfo.contactPersons.${index}.region`}
                            idField={`cp-region-${index}`}
                            label={"contactPerson.region"}
                            provinceOptions={provinceOptions.filter(p => p.country == currentCountry).map(p => ({
                                key: p.codeProvince,
                                value: p.description
                            }))}
                            showSelect={isCreationMode || editing}
                        />
                    </Grid>
                    <Grid item md={6} xs={12}>
                        <CBFormTextFieldController 
                            formName={`cbCorporateInfo.contactPersons.${index}.city`}
                            idField={`cp-city-${index}`}
                            label={"contactPerson.city"}
                            readOnly={isCreationMode ? false : !editing}
                        />
                    </Grid>
                    <Grid item md={6} xs={12}>
                        <CBFormTextFieldController 
                            formName={`cbCorporateInfo.contactPersons.${index}.postalCode`}
                            idField={`cp-postalCode-${index}`}
                            label={"contactPerson.postalCode"}
                            readOnly={isCreationMode ? false : !editing}
                        />
                    </Grid>
                    <Grid item md={6} xs={12}>
                        <CBFormTextFieldController 
                            formName={`cbCorporateInfo.contactPersons.${index}.address`}
                            idField={`cp-address-${index}`}
                            label={"contactPerson.address"}
                            readOnly={isCreationMode ? false : !editing}
                        />
                    </Grid>
                    <Grid item md={6} xs={12}>
                        <CBFormTextFieldController 
                            formName={`cbCorporateInfo.contactPersons.${index}.houseNumber`}
                            idField={`cp-houseNumber-${index}`}
                            label={"contactPerson.houseNumber"}
                            readOnly={isCreationMode ? false : !editing}
                        />
                    </Grid>
                    <Grid item md={6} xs={12}>
                        <CBFormTextFieldController 
                            formName={`cbCorporateInfo.contactPersons.${index}.email`}
                            idField={`cp-email-${index}`}
                            label={"contactPerson.email"}
                            readOnly={isCreationMode ? false : !editing}
                        />
                    </Grid>
                    <Grid item md={6} xs={12}>
                        <Controller 
                            control={control}
                            name={`cbCorporateInfo.contactPersons.${index}.mobile`}
                            render={({field, fieldState}) => (
                                <TextField
                                    {...field}
                                    fullWidth
                                    InputProps={{
                                        readOnly: isCreationMode ? false : !editing,
                                        autoComplete: 'none'
                                    }}
                                    id={`cp-mobile-${index}`}
                                    label={t("contactPerson.mobile")}
                                    value={field.value}
                                    onChange={(event) => {
                                        //if(event.target.value.match(/(^\+?[0-9]+$|^$|^\s$|^-*$)/))
                                            field.onChange(event.target.value);
                                    }}
                                    error={Boolean(fieldState.error)}
                                    helperText={fieldState.error?.message}
                                />
                            )}
                        />
                        
                    </Grid>
                    <Grid item md={6} xs={12}>
                        <CBFormTextFieldController 
                            formName={`cbCorporateInfo.contactPersons.${index}.phoneNumber`}
                            idField={`cp-phoneNumber-${index}`}
                            label={"contactPerson.phoneNumber"}
                            readOnly={isCreationMode ? false : !editing}
                        />
                    </Grid>
                    {/*<Grid item md={6} xs={12}>
                        <TextField 
                            fullWidth
                            InputProps={{
                                readOnly: isCreationMode ? false : !editing
                            }}
                            id={`cp-delete-${index}`}
                            label={t("contactPerson.delete")}
                            {...register(`cbCorporateInfo.contactPersons.${index}.delete`)}
                        />
                    </Grid>*/}
                </Grid>
            </AccordionDetails>
        </Accordion>
    )

}

export default CustomerBaseContactPersonDetails;